// Custom vars of a task (docs/plans/2026-10-02-borden-ontwerp.md §6): a flat
// key -> string | number | boolean map that external systems set through the
// API or MCP ({"source_system":"jira","external_id":"ABC-12"}). Goblic does
// nothing else with them; this is a read-only table with an Edit mode.

import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CUSTOM_VARS_MAX_KEYS, type CustomVarValue, type CustomVars, type Task } from '@goblic/shared'
import { tasksApi } from '../../lib/api-client'
import { Notice } from '../ui'
import { fromRows, toRows, type VarRow, type VarType } from './custom-vars-rows'

function ValueText({ value }: { value: CustomVarValue }) {
  if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
    return (
      <a href={value} target="_blank" rel="noreferrer" className="link break-all">
        {value}
      </a>
    )
  }
  return <span className="break-all text-ink">{String(value)}</span>
}

/** Controlled key / type / value rows with Add and Remove; the caller validates with `fromRows`. */
export function CustomVarsEditor({
  rows,
  onChange,
  emptyText = 'No custom vars yet.',
}: {
  rows: VarRow[]
  onChange: (rows: VarRow[]) => void
  emptyText?: string
}) {
  const setRows = (update: (current: VarRow[]) => VarRow[]) => onChange(update(rows))
  function updateRow(index: number, patch: Partial<VarRow>): void {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }
  return (
    <div className="space-y-3">
      {rows.length === 0 && emptyText ? <p className="text-base text-ink-3">{emptyText}</p> : null}
      <ul className="space-y-2">
        {rows.map((row, index) => (
          <li key={index} className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Key"
              className="field-input w-40 font-mono"
              placeholder="source_system"
              value={row.key}
              onChange={(event) => updateRow(index, { key: event.target.value })}
            />
            <select
              aria-label="Type"
              className="field-input w-auto"
              value={row.type}
              onChange={(event) => {
                const type = event.target.value as VarType
                updateRow(index, { type, value: type === 'boolean' ? 'true' : '' })
              }}
            >
              <option value="string">text</option>
              <option value="number">number</option>
              <option value="boolean">yes / no</option>
            </select>
            {row.type === 'boolean' ? (
              <select
                aria-label="Value"
                className="field-input w-auto"
                value={row.value}
                onChange={(event) => updateRow(index, { value: event.target.value })}
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                aria-label="Value"
                className="field-input min-w-[8rem] flex-1"
                inputMode={row.type === 'number' ? 'decimal' : undefined}
                value={row.value}
                onChange={(event) => updateRow(index, { value: event.target.value })}
              />
            )}
            <button
              type="button"
              className="btn-ghost btn-sm"
              aria-label={`Remove ${row.key || 'var'}`}
              onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn-secondary btn-sm"
        disabled={rows.length >= CUSTOM_VARS_MAX_KEYS}
        onClick={() => setRows((current) => [...current, { key: '', type: 'string', value: '' }])}
      >
        Add var
      </button>
    </div>
  )
}

export function CustomVarsSection({ task }: { task: Task }) {
  const queryClient = useQueryClient()
  const vars = task.customVars ?? {}
  const entries = Object.entries(vars)
  const [editing, setEditing] = useState(false)
  const [rows, setRows] = useState<VarRow[]>([])
  const [error, setError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: (next: CustomVars) =>
      tasksApi.update(task.id, { customVars: next, customVarsMode: 'replace' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task', task.id] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setEditing(false)
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Couldn’t save the custom vars.'),
  })

  function startEdit(): void {
    setRows(toRows(vars))
    setError(null)
    setEditing(true)
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    const result = fromRows(rows)
    if ('error' in result) {
      setError(result.error)
      return
    }
    setError(null)
    saveMutation.mutate(result.vars)
  }

  return (
    <section>
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink-3">Custom vars</h3>
        {!editing ? (
          <button type="button" className="btn-secondary btn-sm" onClick={startEdit}>
            Edit
          </button>
        ) : null}
      </div>

      {editing ? (
        <form className="space-y-3" onSubmit={handleSubmit}>
          <CustomVarsEditor rows={rows} onChange={setRows} />
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      ) : entries.length === 0 ? (
        <p className="text-base text-ink-3">
          None. External systems can set these through the API or MCP, for example to find the task again by an issue id.
        </p>
      ) : (
        <table className="w-full text-base">
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key} className="border-t border-line align-top first:border-t-0">
                <th scope="row" className="w-2/5 py-1.5 pr-3 text-left font-mono text-sm font-normal text-ink-2">
                  {key}
                </th>
                <td className="py-1.5">
                  <ValueText value={value} />
                  {typeof value !== 'string' ? (
                    <span className="ml-1.5 text-sm text-ink-3">{typeof value}</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
